import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  TextField,
  IconButton,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import unifiedApi from '../utils/unifiedApi';
import appConfig from '../config/appConfig';
import { useAuth } from '../contexts/AuthContext';
import { useHasRole, usePermissions } from '../contexts/PermissionsContext';
import { formatJerseyNumber } from '../utils/formatUtils';
import EmergencyContactSection from '../components/player/EmergencyContactSection';
import OutstandingBalanceSection from '../components/player/OutstandingBalanceSection';
import MessageMemberButton from '../components/chat/MessageMemberButton';

const PlayerProfile = () => {
  const { userTenantId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();
  const isSuperAdmin = useHasRole('super-admin');
  const { permissions } = usePermissions();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    postalCode: '',
    jerseySize: '',
    preferredPosition1: '',
    preferredPosition2: ''
  });
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [eventRegistrationsLoading, setEventRegistrationsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [currentUserTenantId, setCurrentUserTenantId] = useState(null);

  // Fetch current user's userTenantId from backend
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentUserTenantId(null);
      return;
    }
    if (currentUserTenantId) return;
    const fetchCurrentUserTenantId = async () => {
      try {
        const userData = await unifiedApi.get('/api/me');
        if (userData?.userTenantId) {
          setCurrentUserTenantId(userData.userTenantId);
        }
      } catch (error) {
        console.warn('Failed to load current user data:', error);
      }
    };
    fetchCurrentUserTenantId();
  }, [isAuthenticated, currentUserTenantId]);

  // Check if user can edit (super admin or own profile)
  const canEdit = isSuperAdmin || (player && currentUserTenantId && parseInt(userTenantId) === currentUserTenantId);

  // Check if user can view contact info (own profile, team managers, league admins, reporters, spares coordinators, or above)
  const isOwnProfile = player && currentUserTenantId && parseInt(userTenantId) === currentUserTenantId;
  const canViewContactInfo = isOwnProfile ||
    permissions?.isSuperAdmin ||
    permissions?.isTenantAdmin ||
    permissions?.isReporter ||
    permissions?.isSparesCoordinator ||
    (player?.teams || []).some(t => permissions?.manageableTeams?.includes(t.teamId)) ||
    (player?.teams || []).some(t => permissions?.manageableLeagues?.includes(t.leagueId)) ||
    (player?.sparePools || []).some(sp => permissions?.manageableLeagues?.includes(sp.leagueId));

  // Check if user can view registrations (self, super admin, tenant admin, spares coordinator, league manager, reporter)
  const canViewRegistrations = isOwnProfile ||
    permissions?.isSuperAdmin ||
    permissions?.isTenantAdmin ||
    permissions?.isSparesCoordinator ||
    permissions?.isReporter ||
    permissions?.manageableLeagues?.length > 0;

  // Emergency contact: self or management roles; the API limits Team Managers to their leagues and the section hides on 403.
  const canViewEmergencyContact = isAuthenticated && (!!isOwnProfile || !!permissions?.canAccessAdmin);

  // Outstanding balance: self, or tenant-level roles (Super/Tenant Admin, tenant Reporter or Spares Coordinator).
  const canViewBalance = isAuthenticated && !!player && (!!isOwnProfile || !!permissions?.canViewAllOutstandingBalances);

  // Build full image URL (relative paths need API base URL)
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url; // Already absolute
    const apiBase = appConfig.apiBaseUrl || 'http://localhost:5131';
    return `${apiBase}${url}`; // Prepend API base URL
  };

  // Format phone number to (###) ###-####
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Format as (###) ###-####
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Return original if not 10 digits
    return phoneNumber;
  };

  // Format date as "Mar 17, 1981"
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Extract just the date part (YYYY-MM-DD) ignoring timezone
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create date using year, month, day (month is 0-indexed)
    const date = new Date(year, month - 1, day);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch player profile using new dedicated endpoint
        const playerData = await unifiedApi.getPlayerProfile(userTenantId);
        
        if (playerData) {
          setPlayer(playerData);
          setEditedData({
            firstName: playerData.firstName || '',
            lastName: playerData.lastName || '',
            email: playerData.email || '',
            phoneNumber: playerData.phoneNumber || '',
            dateOfBirth: playerData.dateOfBirth ? new Date(playerData.dateOfBirth).toISOString().split('T')[0] : '',
            postalCode: playerData.postalCode || '',
            jerseySize: playerData.jerseySize || '',
            preferredPosition1: playerData.preferredPosition1 || '',
            preferredPosition2: playerData.preferredPosition2 || ''
          });
        } else {
          setError('Player not found');
        }
      } catch (err) {
        console.error('Error fetching player data:', err);
        setError(err.message || 'Failed to load player information');
      } finally {
        setLoading(false);
      }
    };

    const fetchPlayerStats = async () => {
      try {
        setStatsLoading(true);
        
        // Fetch player statistics with 5-minute cache
        const statsData = await unifiedApi.getPlayerStatsCached(userTenantId);
        
        if (statsData) {
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching player stats:', err);
        // Don't set error state - stats are optional
      } finally {
        setStatsLoading(false);
      }
    };

    if (userTenantId) {
      fetchPlayerData();
      fetchPlayerStats();
    }
  }, [userTenantId]);

  // Fetch registrations when player is loaded and user has permission
  useEffect(() => {
    // Reset on navigation to prevent stale data
    setRegistrations([]);
    setRegistrationsLoading(false);

    const fetchRegistrations = async () => {
      setRegistrationsLoading(true);
      try {
        const data = await unifiedApi.getPlayerRegistrations(userTenantId);
        setRegistrations(data || []);
      } catch (err) {
        // Registrations are optional - don't show error
        console.error('Error fetching registrations:', err);
      } finally {
        setRegistrationsLoading(false);
      }
    };

    if (userTenantId && player && canViewRegistrations) {
      fetchRegistrations();
    }
  }, [userTenantId, player, canViewRegistrations]);

  // Fetch event registrations
  useEffect(() => {
    setEventRegistrations([]);
    setEventRegistrationsLoading(false);

    const fetchEventRegistrations = async () => {
      setEventRegistrationsLoading(true);
      try {
        const data = isOwnProfile
          ? await unifiedApi.getMyEventRegistrations()
          : await unifiedApi.getPlayerEventRegistrations(userTenantId);
        setEventRegistrations(data || []);
      } catch (err) {
        console.error('Error fetching event registrations:', err);
      } finally {
        setEventRegistrationsLoading(false);
      }
    };

    if (userTenantId && canViewRegistrations) {
      fetchEventRegistrations();
    }
  }, [isOwnProfile, userTenantId, canViewRegistrations]);

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
    // Reset to original values
    setEditedData({
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      phoneNumber: player.phoneNumber || '',
      dateOfBirth: player.dateOfBirth ? new Date(player.dateOfBirth).toISOString().split('T')[0] : '',
      postalCode: player.postalCode || '',
      jerseySize: player.jerseySize || '',
      preferredPosition1: player.preferredPosition1 || '',
      preferredPosition2: player.preferredPosition2 || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      // Convert empty strings to null for nullable fields before sending
      const payload = {
        ...editedData,
        dateOfBirth: editedData.dateOfBirth || null,
        phoneNumber: editedData.phoneNumber || null,
        postalCode: editedData.postalCode || null,
        jerseySize: editedData.jerseySize || null,
        preferredPosition1: editedData.preferredPosition1 || null,
        preferredPosition2: editedData.preferredPosition2 || null
      };

      // Call API to update player info
      await unifiedApi.updatePlayerProfile(userTenantId, payload);

      // Update local state
      setPlayer({
        ...player,
        ...editedData
      });

      setIsEditing(false);
    } catch (err) {
      console.error('Error saving player data:', err);
      setSaveError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoClick = () => {
    if (canEdit && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setPhotoError('Please select an image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Image size cannot exceed 10MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setPhotoError(null);

      const result = await unifiedApi.uploadPlayerPhoto(userTenantId, file);

      if (result?.url) {
        // Update local state with new photo URL (add timestamp to bust cache)
        setPlayer(prev => ({
          ...prev,
          profilePhotoUrl: `${result.url}?t=${Date.now()}`
        }));
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
      setPhotoError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!player) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Player profile not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      {/* Player Profile Card */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        {/* Edit/Save/Cancel Buttons */}
        {canEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
            {!isEditing ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                size="small"
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  size="small"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                  size="small"
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {photoError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhotoError(null)}>
            {photoError}
          </Alert>
        )}

        {/* Hidden file input for photo upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePhotoUpload}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
        />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: 3 }}>
          <Box sx={{ position: 'relative', mr: { xs: 0, sm: 3 }, mb: { xs: 2, sm: 0 } }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                canEdit && (
                  <IconButton
                    onClick={handlePhotoClick}
                    disabled={uploadingPhoto}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.400',
                      },
                    }}
                  >
                    {uploadingPhoto ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CameraAltIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                )
              }
            >
              <Avatar
                src={getImageUrl(player.profilePhotoUrl)}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  bgcolor: 'primary.main',
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  cursor: canEdit ? 'pointer' : 'default',
                }}
                onClick={handlePhotoClick}
              >
                {(isEditing ? editedData.firstName : player.firstName)?.[0]}{(isEditing ? editedData.lastName : player.lastName)?.[0]}
              </Avatar>
            </Badge>
          </Box>
          <Box sx={{ flex: 1 }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="First Name"
                    value={editedData.firstName}
                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Last Name"
                    value={editedData.lastName}
                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Email: {player.email}
                </Typography>
                <TextField
                  label="Phone Number"
                  value={editedData.phoneNumber}
                  onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Date of Birth"
                  type="date"
                  value={editedData.dateOfBirth}
                  onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Postal Code"
                  value={editedData.postalCode}
                  onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                  size="small"
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="jersey-size-label">Jersey Size</InputLabel>
                  <Select
                    labelId="jersey-size-label"
                    id="jersey-size-select"
                    value={editedData.jerseySize}
                    label="Jersey Size"
                    onChange={(e) => handleFieldChange('jerseySize', e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="S">S</MenuItem>
                    <MenuItem value="M">M</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="XL">XL</MenuItem>
                    <MenuItem value="XXL">XXL</MenuItem>
                    <MenuItem value="XXXL">XXXL</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="preferred-position-1-label">Preferred Position 1</InputLabel>
                    <Select
                      labelId="preferred-position-1-label"
                      id="preferred-position-1-select"
                      value={editedData.preferredPosition1}
                      label="Preferred Position 1"
                      onChange={(e) => handleFieldChange('preferredPosition1', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Striker">Striker</MenuItem>
                      <MenuItem value="Midfield">Midfield</MenuItem>
                      <MenuItem value="Defender">Defender</MenuItem>
                      <MenuItem value="Keeper">Keeper</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="preferred-position-2-label">Preferred Position 2</InputLabel>
                    <Select
                      labelId="preferred-position-2-label"
                      id="preferred-position-2-select"
                      value={editedData.preferredPosition2}
                      label="Preferred Position 2"
                      onChange={(e) => handleFieldChange('preferredPosition2', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Striker">Striker</MenuItem>
                      <MenuItem value="Midfield">Midfield</MenuItem>
                      <MenuItem value="Defender">Defender</MenuItem>
                      <MenuItem value="Keeper">Keeper</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                  {player.firstName} {player.lastName}
                </Typography>
                {!isOwnProfile && (
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <MessageMemberButton userTenantId={userTenantId} firstName={player.firstName} />
                  </Box>
                )}
                {canViewContactInfo && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, textAlign: { xs: 'center', sm: 'left' } }}>
                    {player.email || 'N/A'}
                  </Typography>
                )}
                {canViewContactInfo && player.phoneNumber && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, textAlign: { xs: 'center', sm: 'left' } }}>
                    {formatPhoneNumber(player.phoneNumber)}
                  </Typography>
                )}
                {canViewContactInfo && player.dateOfBirth && (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    {formatDate(player.dateOfBirth)}
                  </Typography>
                )}
                {canViewContactInfo && player.postalCode && (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    Postal Code: {player.postalCode}
                  </Typography>
                )}
                {canViewContactInfo && player.jerseySize && (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    Jersey Size: {player.jerseySize}
                  </Typography>
                )}
                {canViewContactInfo && (player.preferredPosition1 || player.preferredPosition2) && (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    Preferred: {[player.preferredPosition1, player.preferredPosition2].filter(Boolean).join(', ')}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        {canViewEmergencyContact && (
          <EmergencyContactSection userTenantId={userTenantId} canEdit={!!isOwnProfile} />
        )}

        {canViewBalance && (
          <OutstandingBalanceSection userTenantId={userTenantId} isOwnProfile={!!isOwnProfile} />
        )}

        <Divider sx={{ my: 3 }} />

        {/* Summary Statistics - deduplicated using game IDs */}
        {!statsLoading && (() => {
          // Deduplicate games across all stats entries using unique game IDs
          const allGameIds = new Set();
          let totalGoals = 0;
          let totalCards = 0;
          (stats?.leagueStats || []).forEach(s => {
            const gameIds = s.gameIds || [];
            gameIds.forEach(gid => allGameIds.add(gid));
            totalGoals += s.goals;
            totalCards += s.yellowCards + s.redCards;
          });
          // Count seasons that include at least one actual appearance (regular or spare).
          // Leagues where the player is only rostered/in a spare pool but has not played are excluded.
          const allSeasonIds = new Set();
          (stats?.leagueStats || []).forEach(s => {
            if ((s.gamesPlayed || 0) > 0) allSeasonIds.add(s.leagueId);
          });
          const totalSeasons = allSeasonIds.size;

          if (totalSeasons === 0 && allGameIds.size === 0) return null;

          return (
            <>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEventsIcon sx={{ mr: 1 }} />
                Career Stats
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: { xs: 1, sm: 2 }, 
                mb: 3
              }}>
                <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {allGameIds.size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Games
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {totalSeasons}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seasons
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {totalGoals}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Goals
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {totalCards}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cards
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 3 }} />
            </>
          );
        })()}

        {/* Teams & Season Stats Section */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SportsIcon sx={{ mr: 1 }} />
          Teams & Season Stats
        </Typography>
        {(() => {
          // Build unified display list: current teams, historical teams from stats, and spare pools
          const displayItems = [];

          // 1. Current team memberships
          (player.teams || []).forEach(team => {
            const teamStats = stats?.leagueStats?.find(
              s => s.teamId === team.teamId && s.leagueId === team.leagueId && !s.isSpare
            );
            // Spare stats only for THIS team (spare appearances on the same team the player is rostered on).
            // Spare appearances for OTHER teams in the same league get their own cards below.
            const spareStatsForTeam = stats?.leagueStats?.filter(
              s => s.leagueId === team.leagueId && s.teamId === team.teamId && s.isSpare
            ) || [];
            const isSpareForSeason = player.sparePools?.some(sp => sp.leagueId === team.leagueId);
            const spareGames = spareStatsForTeam.reduce((sum, s) => sum + s.gamesPlayed, 0);
            const spareGoals = spareStatsForTeam.reduce((sum, s) => sum + s.goals, 0);
            const teamGameIds = [
              ...(teamStats?.gameIds || []),
              ...spareStatsForTeam.flatMap(s => s.gameIds || []),
            ];
            const maxGameId = teamGameIds.length ? Math.max(...teamGameIds) : 0;

            displayItems.push({
              type: 'team', key: `team-${team.teamId}-${team.leagueId}`,
              team, teamStats, spareStatsForTeam, isSpareForSeason, spareGames, spareGoals, maxGameId, leagueId: team.leagueId,
            });
          });

          // 2. Historical teams from stats (non-spare) not in current player.teams
          (stats?.leagueStats || []).filter(s => !s.isSpare).forEach(s => {
            const hasTeam = player.teams?.some(t => t.teamId === s.teamId && t.leagueId === s.leagueId);
            if (!hasTeam) {
              const maxGameId = s.gameIds?.length ? Math.max(...s.gameIds) : 0;
              displayItems.push({
                type: 'historical-team', key: `hist-${s.teamId}-${s.leagueId}`,
                teamStats: s, maxGameId, leagueId: s.leagueId,
              });
            }
          });

          // 2b. Spare-only teams: spare appearances for a team the player is NOT currently rostered on.
          // These should each get their own card with a "Spare" indicator, regardless of whether the
          // player is currently rostered on a different team in the same league.
          (stats?.leagueStats || []).filter(s => s.isSpare).forEach(s => {
            const isCurrentTeam = player.teams?.some(t => t.teamId === s.teamId && t.leagueId === s.leagueId);
            if (!isCurrentTeam) {
              const maxGameId = s.gameIds?.length ? Math.max(...s.gameIds) : 0;
              displayItems.push({
                type: 'spare-team', key: `spare-team-${s.teamId}-${s.leagueId}`,
                teamStats: s, maxGameId, leagueId: s.leagueId,
              });
            }
          });

          // 3. Spare pools for leagues where player has no team
          const leagueIdsWithTeams = new Set((player.teams || []).map(t => t.leagueId));
          (player.sparePools || []).forEach(sp => {
            if (!leagueIdsWithTeams.has(sp.leagueId)) {
              const spareStats = (stats?.leagueStats || []).filter(s => s.leagueId === sp.leagueId && s.isSpare);
              const allSpareGameIds = spareStats.flatMap(s => s.gameIds || []);
              const maxGameId = allSpareGameIds.length ? Math.max(...allSpareGameIds) : 0;
              displayItems.push({
                type: 'spare-pool', key: `spare-${sp.leagueId}`,
                sparePool: sp, spareStats, maxGameId, leagueId: sp.leagueId,
              });
            }
          });

          // Sort by maxGameId descending (most recent activity first)
          displayItems.sort((a, b) => b.maxGameId - a.maxGameId);

          if (displayItems.length === 0) {
            return (
              <Alert severity="info" sx={{ mt: 2 }}>
                Not currently on any teams
              </Alert>
            );
          }

          return (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2, 
              mt: 2 
            }}>
              {displayItems.map((item) => {
                // --- Current team card ---
                if (item.type === 'team') {
                  const { team, teamStats, isSpareForSeason, spareGames, spareGoals } = item;
                  return (
                    <Card key={item.key} variant="outlined" sx={{ minWidth: 0 }}>
                      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            cursor: team.teamSlug ? 'pointer' : 'default',
                            '&:hover': team.teamSlug ? { color: 'primary.main', textDecoration: 'underline' } : {},
                          }}
                          onClick={() => team.teamSlug && navigate(`/teams/${team.teamSlug}`)}
                        >
                          {team.teamName}
                        </Typography>
                        {team.leagueName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {team.leagueName}
                          </Typography>
                        )}
                        {teamStats && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, py: 1, px: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.gamesPlayed}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Games</Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.goals}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Goals</Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.yellowCards + teamStats.redCards}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Cards</Typography>
                            </Box>
                          </Box>
                        )}
                        {isSpareForSeason && spareGames > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, py: 1, px: 1, bgcolor: 'warning.50', border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.dark">{spareGames}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Spare Games</Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.dark">{spareGoals}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Spare Goals</Typography>
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {team.jerseyNumber && (
                            <Chip label={`#${formatJerseyNumber(team.jerseyNumber)}`} size="small" color="primary" onClick={() => {/* do nothing */}} />
                          )}
                          {team.position && (
                            <Chip label={team.position} size="small" variant="outlined" onClick={() => {/* do nothing */}} />
                          )}
                          {team.teamRole && (
                            <Chip label={team.teamRole} size="small" color="secondary" onClick={() => {/* do nothing */}} />
                          )}
                          {isSpareForSeason && (
                            <Chip label="Spare" size="small" color="warning" onClick={() => {/* do nothing */}} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                // --- Historical team card (former team from stats) ---
                if (item.type === 'historical-team') {
                  const { teamStats } = item;
                  return (
                    <Card key={item.key} variant="outlined" sx={{ minWidth: 0 }}>
                      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {teamStats.teamName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {teamStats.leagueName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, py: 1, px: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.gamesPlayed}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Games</Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.goals}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Goals</Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="primary.main">{teamStats.yellowCards + teamStats.redCards}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Cards</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip label="Former" size="small" variant="outlined" color="default" onClick={() => {/* do nothing */}} />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                // --- Spare-only team card (played as a spare for this team; not on its current roster) ---
                if (item.type === 'spare-team') {
                  const { teamStats } = item;
                  return (
                    <Card key={item.key} variant="outlined" sx={{ minWidth: 0, borderColor: 'warning.main', borderStyle: 'dashed' }}>
                      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {teamStats.teamName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {teamStats.leagueName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, py: 1, px: 1, bgcolor: 'warning.50', border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="warning.dark">{teamStats.gamesPlayed}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Spare Games</Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="warning.dark">{teamStats.goals}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Spare Goals</Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body1" fontWeight="bold" color="warning.dark">{teamStats.yellowCards + teamStats.redCards}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Cards</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip label="Spare" size="small" color="warning" clickable={false} onClick={() => {/* do nothing */}} />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                // --- Spare pool card (league where player has no team, only spare pool) ---
                if (item.type === 'spare-pool') {
                  const { sparePool, spareStats } = item;
                  const totalGames = spareStats.reduce((sum, s) => sum + s.gamesPlayed, 0);
                  const totalGoals = spareStats.reduce((sum, s) => sum + s.goals, 0);
                  const totalCards = spareStats.reduce((sum, s) => sum + s.yellowCards + s.redCards, 0);

                  return (
                    <Card key={item.key} variant="outlined" sx={{ minWidth: 0, borderColor: 'warning.main', borderStyle: 'dashed' }}>
                      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {sparePool.leagueName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Spare Pool
                        </Typography>
                        {totalGames > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, py: 1, px: 1, bgcolor: 'warning.50', border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.dark">{totalGames}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Games</Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.dark">{totalGoals}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Goals</Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.dark">{totalCards}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Cards</Typography>
                            </Box>
                          </Box>
                        )}
                        {spareStats.length > 1 && spareStats.map(s => (
                          <Box key={s.teamId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                              {s.teamName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {s.gamesPlayed}G &middot; {s.goals} goals
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: spareStats.length > 1 ? 1 : 0 }}>
                          <Chip label="Spare" size="small" color="warning" onClick={() => {/* do nothing */}} />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                return null;
              })}
            </Box>
          );
        })()}
      </Paper>

      {/* My Registrations Section */}
      {canViewRegistrations && ((!registrationsLoading && registrations.length > 0) || (!eventRegistrationsLoading && eventRegistrations.length > 0)) && (
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1 }} />
            My Registrations
          </Typography>

          {!registrationsLoading && registrations.length > 0 && (
            <>
              {(!eventRegistrationsLoading && eventRegistrations.length > 0) && (
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>League Registrations</Typography>
              )}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {registrations.map((reg) => (
                    <Card key={reg.id} variant="outlined" sx={{ minWidth: 0 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {reg.leagueName}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Position</Typography>
                          <Typography variant="body2">{reg.positionType.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                          <Typography variant="caption" color="text.secondary">Registered</Typography>
                          <Typography variant="body2">{formatDate(reg.createdAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">Cost</Typography>
                          <Typography variant="body2">${(reg.priceInCents / 100).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box>
                            <Chip
                              label={reg.status}
                              size="small"
                              color={reg.status === 'Paid' ? 'success' : 'default'}
                              clickable={false}
                              onClick={() => {}}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>League</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Registered</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {registrations.map((reg) => (
                        <TableRow key={reg.id}>
                          <TableCell>{reg.leagueName}</TableCell>
                          <TableCell>{reg.positionType.replace(/([A-Z])/g, ' $1').trim()}</TableCell>
                          <TableCell>{formatDate(reg.createdAt)}</TableCell>
                          <TableCell align="right">${(reg.priceInCents / 100).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={reg.status}
                              size="small"
                              color={reg.status === 'Paid' ? 'success' : 'default'}
                              clickable={false}
                              onClick={() => {}}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {eventRegistrationsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!eventRegistrationsLoading && eventRegistrations.length > 0 && (
            <>
              {registrations.length > 0 && (
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Event Registrations</Typography>
              )}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {eventRegistrations.map((reg) => (
                    <Card key={reg.id} variant="outlined" sx={{ minWidth: 0 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {reg.eventName}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Registered</Typography>
                          <Typography variant="body2">{formatDate(reg.createdAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">Cost</Typography>
                          <Typography variant="body2">${(reg.priceInCents / 100).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box>
                            <Chip
                              label={reg.status}
                              size="small"
                              color={reg.status === 'Paid' ? 'success' : reg.status === 'Refunded' ? 'warning' : 'default'}
                              clickable={false}
                              onClick={() => {}}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Registered</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventRegistrations.map((reg) => (
                        <TableRow key={reg.id}>
                          <TableCell>{reg.eventName}</TableCell>
                          <TableCell>{formatDate(reg.createdAt)}</TableCell>
                          <TableCell align="right">${(reg.priceInCents / 100).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={reg.status}
                              size="small"
                              color={reg.status === 'Paid' ? 'success' : reg.status === 'Refunded' ? 'warning' : 'default'}
                              clickable={false}
                              onClick={() => {}}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default PlayerProfile;
